import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowRight, FaBell, FaFileContract, FaFileUpload, FaTimes } from 'react-icons/fa';
import useMissingDocuments, {
  SOP_PROMPT_SESSION_KEY,
  readSessionFlag,
  writeSessionFlag,
} from './useMissingDocuments';
import styles from './LoginReminders.module.css';

const MAX_VISIBLE_SOPS = 4;

const ReminderSection = ({ tone, icon, title, count, description, actionLabel, onAction, children }) => (
  <section className={`${styles.section} ${styles[tone]}`}>
    <div className={styles.sectionHeader}>
      <span className={styles.sectionIcon}>{icon}</span>
      <div className={styles.sectionHeading}>
        <h3 className={styles.sectionTitle}>
          {title}
          <span className={styles.sectionCount}>{count}</span>
        </h3>
        <p className={styles.sectionDescription}>{description}</p>
      </div>
    </div>
    <ul className={styles.list}>{children}</ul>
    <button type="button" className={styles.sectionAction} onClick={onAction}>
      {actionLabel} <FaArrowRight />
    </button>
  </section>
);

/**
 * Girişte bir kez çıkan hatırlatmaları (okunmamış SOP'lar, eksik CV/GCP/IATA
 * belgeleri) TEK bir pencerede toplar. Pencere, iki kontrol de sonuçlandıktan
 * sonra açılır; böylece hatırlatmalar üst üste binmez ve sonradan büyümez.
 */
const LoginReminders = ({ unreadSOPs, sopsLoaded, onGoToSops }) => {
  const navigate = useNavigate();
  const { missing, settled, signedIn, markShown } = useMissingDocuments();
  // null: kapalı; aksi halde hangi bölümlerin gösterileceği
  const [sections, setSections] = useState(null);

  useEffect(() => {
    if (!signedIn) {
      setSections(null);
      return;
    }
    if (sections || !sopsLoaded || !settled) return;

    const showSops = unreadSOPs.length > 0 && !readSessionFlag(SOP_PROMPT_SESSION_KEY);
    const showDocs = missing.length > 0;
    if (!showSops && !showDocs) return;

    setSections({ sops: showSops, docs: showDocs });
  }, [signedIn, sections, sopsLoaded, settled, unreadSOPs.length, missing.length]);

  // "Gösterildi" işareti pencere açılınca değil, kullanıcı kapatınca yazılır:
  // Layout her rotada yeniden kurulduğundan, görülmeden kaybolan bir pencere
  // sonraki sayfada yeniden açılır.
  const close = useCallback(() => {
    if (sections?.sops) writeSessionFlag(SOP_PROMPT_SESSION_KEY);
    if (sections?.docs) markShown();
    setSections({ sops: false, docs: false });
  }, [sections, markShown]);

  const isOpen = Boolean(sections && (sections.sops || sections.docs));

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, close]);

  if (!isOpen) return null;

  const goToSops = () => {
    close();
    onGoToSops();
  };

  const goToDocuments = () => {
    close();
    // İlk eksik belgenin sekmesi açık gelsin
    navigate('/profile', { state: { activeTab: missing[0].profileTab } });
  };

  const topicCount = Number(sections.sops) + Number(sections.docs);

  return (
    <div className={styles.overlay} onClick={close}>
      <div
        className={styles.dialog}
        onClick={(e) => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="login-reminders-title"
      >
        <div className={styles.header}>
          <span className={styles.headerIcon}>
            <FaBell />
          </span>
          <div className={styles.headerText}>
            <h2 id="login-reminders-title" className={styles.title}>
              Bekleyen işleriniz var
            </h2>
            <p className={styles.subtitle}>
              {topicCount > 1
                ? `İlgilenmeniz gereken ${topicCount} konu var.`
                : 'İlgilenmeniz gereken bir konu var.'}
            </p>
          </div>
          <button type="button" className={styles.close} onClick={close} aria-label="Kapat">
            <FaTimes />
          </button>
        </div>

        <div className={styles.body}>
          {sections.sops && (
            <ReminderSection
              tone="toneDanger"
              icon={<FaFileContract />}
              title="Okunmamış SOP'lar"
              count={unreadSOPs.length}
              description="Okundu olarak işaretlediğiniz SOP'lar bu listeden çıkar."
              actionLabel="SOP'lara git"
              onAction={goToSops}
            >
              {unreadSOPs.slice(0, MAX_VISIBLE_SOPS).map((item) => (
                <li key={item.id} className={styles.item}>
                  <span className={styles.itemName}>{item.name}</span>
                  {item.dueDate && (
                    <span className={`${styles.badge} ${item.dueSoon ? styles.badgeUrgent : ''}`}>
                      {item.deadlineLabel}
                    </span>
                  )}
                </li>
              ))}
              {unreadSOPs.length > MAX_VISIBLE_SOPS && (
                <li className={styles.more}>+{unreadSOPs.length - MAX_VISIBLE_SOPS} SOP daha</li>
              )}
            </ReminderSection>
          )}

          {sections.docs && (
            <ReminderSection
              tone="toneWarning"
              icon={<FaFileUpload />}
              title="Eksik belgeler"
              count={missing.length}
              description="Profilinizdeki CV/GCP/IATA bölümünden yükleyebilirsiniz."
              actionLabel="Belgelerimi yükle"
              onAction={goToDocuments}
            >
              {missing.map((type) => (
                <li key={type.key} className={styles.item}>
                  <span className={styles.itemName}>{type.label}</span>
                  <span className={`${styles.badge} ${styles.badgeWarning}`}>Yüklenmedi</span>
                </li>
              ))}
            </ReminderSection>
          )}
        </div>

        <div className={styles.footer}>
          <button type="button" className={styles.later} onClick={close}>
            Daha sonra
          </button>
        </div>
      </div>
    </div>
  );
};

export default LoginReminders;
